import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { createMuiTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);

  const toggleTheme = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  const theme = useMemo(
    () =>
      createMuiTheme({
        typography: {
          fontFamily:
            '"Segoe UI Variable", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
          h5: {
            fontWeight: 700,
            letterSpacing: "-0.02em"
          },
          h6: {
            fontWeight: 700,
            letterSpacing: "-0.01em"
          },
          subtitle1: {
            fontWeight: 600
          },
          button: {
            fontWeight: 700,
            textTransform: "none"
          }
        },
        palette: {
          type: darkMode ? "dark" : "light",
          primary: {
            main: darkMode ? "#32b8a8" : "#176b63",
            light: darkMode ? "#5fd1c4" : "#2a8b81",
            dark: darkMode ? "#1e8b7e" : "#11524d",
            contrastText: "#ffffff",
          },
          secondary: {
            main: darkMode ? "#d8a34f" : "#a87422",
            light: darkMode ? "#ebc884" : "#c8923f",
            dark: darkMode ? "#bc8c3e" : "#7d5718",
            contrastText: "#ffffff",
          },
          info: {
            main: darkMode ? "#7aa7f3" : "#3567c8",
            light: darkMode ? "#9bc0fb" : "#5e8ce2",
            dark: darkMode ? "#5a8ae7" : "#274fa0",
            contrastText: "#ffffff",
          },
          divider: darkMode ? "rgba(148, 163, 184, 0.14)" : "#dbe3ea",
          background: darkMode
            ? {
                default: "#0d131a",
                paper: "#111a23",
              }
            : {
                default: "#f3f6f8",
                paper: "#ffffff",
              },
          text: darkMode
            ? {
                primary: "#e8edf3",
                secondary: "#98a6b8",
              }
            : {
                primary: "#17202b",
                secondary: "#617082",
              },
        },
        shape: {
          borderRadius: 16,
        },
        scrollbarStyles: {
          scrollbarWidth: "thin",
          scrollbarColor: darkMode
            ? "#3f7e74 rgba(8, 14, 22, 0.22)"
            : "#9ab8b1 rgba(21, 43, 55, 0.08)",
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: darkMode
              ? "rgba(8, 14, 22, 0.22)"
              : "rgba(21, 43, 55, 0.08)",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-thumb": {
            boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.20)",
            backgroundColor: darkMode ? "#3f7e74" : "#9ab8b1",
            borderRadius: 999,
          },
        },
        overrides: {
          MuiCssBaseline: {
            "@global": {
            body: {
              background: darkMode
                  ? "linear-gradient(180deg, #0a1017 0%, #0f1720 46%, #141e29 100%)"
                  : "linear-gradient(180deg, #f7f9fb 0%, #f1f5f8 44%, #edf2f6 100%)",
              },
              "#root": {
                minHeight: "100vh"
              },
              "*": {
                scrollbarWidth: "thin",
                scrollbarColor: darkMode
                  ? "#3f7e74 rgba(8, 14, 22, 0.22)"
                  : "#9ab8b1 rgba(21, 43, 55, 0.08)"
              },
              "*::-webkit-scrollbar": {
                width: "6px",
                height: "6px"
              },
              "*::-webkit-scrollbar-track": {
                background: darkMode
                  ? "rgba(8, 14, 22, 0.22)"
                  : "rgba(21, 43, 55, 0.08)",
                borderRadius: 999
              },
              "*::-webkit-scrollbar-thumb": {
                backgroundColor: darkMode ? "#3f7e74" : "#9ab8b1",
                borderRadius: 999,
                border: darkMode
                  ? "1px solid rgba(8, 14, 22, 0.24)"
                  : "1px solid rgba(255, 255, 255, 0.35)"
              },
              "*::-webkit-scrollbar-thumb:hover": {
                backgroundColor: darkMode ? "#519389" : "#7ea79d"
              }
            },
          },
          MuiAppBar: {
            colorPrimary: {
              background: darkMode
                ? "linear-gradient(180deg, rgba(11,17,24,0.98) 0%, rgba(16,24,33,0.98) 100%)"
                : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
              color: darkMode ? "#e8edf3" : "#17202b",
              boxShadow: darkMode
                ? "0 12px 30px rgba(2, 6, 23, 0.28)"
                : "0 10px 30px rgba(15, 23, 42, 0.08)",
              borderBottom: darkMode
                ? "1px solid rgba(71, 85, 105, 0.28)"
                : "1px solid rgba(148, 163, 184, 0.16)",
            },
          },
          MuiDrawer: {
            paper: {
              background: darkMode
                ? "linear-gradient(180deg, rgba(11,17,24,0.98) 0%, rgba(15,23,32,0.98) 100%)"
                : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,249,251,0.98) 100%)",
              borderRight: darkMode
                ? "1px solid rgba(71, 85, 105, 0.28)"
                : "1px solid rgba(148, 163, 184, 0.18)",
            },
          },
          MuiPaper: {
            rounded: {
              borderRadius: 20,
            },
            outlined: {
              borderColor: darkMode
                ? "rgba(148, 163, 184, 0.14)"
                : "rgba(148, 163, 184, 0.18)",
            },
            elevation1: {
              boxShadow: darkMode
                ? "0 18px 42px rgba(2, 6, 23, 0.26)"
                : "0 18px 40px rgba(15, 23, 42, 0.06)",
            },
          },
          MuiButton: {
            root: {
              borderRadius: 13,
              fontWeight: 700,
              boxShadow: "none",
              paddingLeft: 18,
              paddingRight: 18,
            },
            containedPrimary: {
              background: darkMode
                ? "linear-gradient(180deg, #2aa092 0%, #176b63 100%)"
                : "linear-gradient(180deg, #22877d 0%, #176b63 100%)",
              color: "#f8fffb",
              boxShadow: darkMode
                ? "0 10px 24px rgba(23, 107, 99, 0.24)"
                : "0 10px 24px rgba(23, 107, 99, 0.18)",
            },
            outlinedPrimary: {
              borderColor: darkMode
                ? "rgba(80, 189, 176, 0.26)"
                : "rgba(23, 107, 99, 0.24)",
            },
          },
          MuiOutlinedInput: {
            root: {
              backgroundColor: darkMode
                ? "rgba(17, 26, 35, 0.82)"
                : "rgba(255, 255, 255, 0.8)",
              borderRadius: 16,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: darkMode
                  ? "rgba(148, 163, 184, 0.20)"
                  : "rgba(148, 163, 184, 0.26)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: darkMode
                  ? "rgba(174, 188, 204, 0.30)"
                  : "rgba(96, 112, 130, 0.38)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: darkMode ? "#32b8a8" : "#176b63",
                borderWidth: 1,
              },
            },
            input: {
              paddingTop: 15,
              paddingBottom: 15,
            },
          },
          MuiTableCell: {
            head: {
              color: darkMode ? "#9fb0c3" : "#556476",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              borderBottom: darkMode
                ? "1px solid rgba(71, 85, 105, 0.30)"
                : "1px solid rgba(148, 163, 184, 0.18)",
            },
            body: {
              borderBottom: darkMode
                ? "1px solid rgba(51, 65, 85, 0.55)"
                : "1px solid rgba(226, 232, 240, 0.7)",
            },
          },
          MuiDialog: {
            paper: {
              borderRadius: 24,
              boxShadow: darkMode
                ? "0 30px 60px rgba(2, 6, 23, 0.36)"
                : "0 30px 60px rgba(15, 23, 42, 0.16)",
            },
          },
          MuiMenu: {
            paper: {
              borderRadius: 16,
              border: darkMode
                ? "1px solid rgba(71, 85, 105, 0.28)"
                : "1px solid rgba(148, 163, 184, 0.16)",
              boxShadow: darkMode
                ? "0 18px 40px rgba(2, 6, 23, 0.28)"
                : "0 18px 38px rgba(15, 23, 42, 0.10)",
            },
          },
          MuiTab: {
            root: {
              textTransform: "none",
              fontWeight: 700,
              minHeight: 56,
            },
          },
          MuiChip: {
            root: {
              fontWeight: 700,
              borderRadius: 999,
            },
          },
        MuiBadge: {
          colorPrimary: {
            backgroundColor: darkMode ? "#32b8a8" : "#176b63",
          },
          colorSecondary: {
            backgroundColor: darkMode ? "#d8a34f" : "#a87422",
          },
        },
      },
      }),
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
